import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Button } from '@/components/atoms';
import { geoApi, type DiaClima } from '@/services/geo';
import { intelligenceService } from '@/services/intelligence';

import styles from './Mapa.module.css';

// Cartograma de tiles (linha, coluna) — Norte no topo, Sul em baixo.
const TILES: Record<string, [number, number]> = {
  RR: [1, 4], AP: [1, 6],
  AM: [2, 2], PA: [2, 4], MA: [2, 5], CE: [2, 6], RN: [2, 7],
  AC: [3, 1], RO: [3, 2], MT: [3, 3], TO: [3, 4], PI: [3, 5], PE: [3, 6], PB: [3, 7],
  MS: [4, 3], GO: [4, 4], BA: [4, 5], SE: [4, 6], AL: [4, 7],
  DF: [5, 3], MG: [5, 4], ES: [5, 5],
  SP: [6, 3], RJ: [6, 4],
  PR: [7, 2], SC: [7, 3],
  RS: [8, 2],
};

function pesoClass(peso: string): string {
  if (peso === 'alta') return styles.pesoAlta;
  if (peso === 'media') return styles.pesoMedia;
  return styles.pesoEmergente;
}

function WeatherDay({ label, d }: { label: string; d: DiaClima }) {
  return (
    <div className={styles.wxDay}>
      <span className={styles.wxLabel}>{label}</span>
      <span className={styles.wxTemp}>
        {d.tmax != null ? `${Math.round(d.tmax)}°` : '—'}
        <span className={styles.wxMin}>{d.tmin != null ? ` / ${Math.round(d.tmin)}°` : ''}</span>
      </span>
      <span className={styles.wxCond}>{d.condicao}</span>
      <span className={styles.wxMeta}>
        chuva {d.precip != null ? `${Math.round(d.precip)} mm` : '—'}
        {d.prob_chuva != null ? ` (${Math.round(d.prob_chuva)}%)` : ''}
      </span>
      <span className={styles.wxMeta}>
        vento {d.vento != null ? `${Math.round(d.vento)}` : '—'}
        {d.rajada != null ? ` (raj. ${Math.round(d.rajada)})` : ''} km/h
      </span>
      <span className={styles.wxMeta}>
        UV {d.uv != null ? Math.round(d.uv) : '—'}
        {d.nascer && d.por ? ` · sol ${d.nascer}–${d.por}` : ''}
      </span>
    </div>
  );
}

export function Mapa() {
  const [uf, setUf] = useState<string | null>(null);
  // "Rodar fastnews" gera vários rascunhos do estado — confirma em 2 cliques.
  const [armedUf, setArmedUf] = useState<string | null>(null);

  const cidadesQ = useQuery({ queryKey: ['geo', 'cidades'], queryFn: geoApi.getCidades });
  const cfgQ = useQuery({ queryKey: ['geo', 'estados-config'], queryFn: geoApi.getEstadosConfig });
  const climaQ = useQuery({
    queryKey: ['geo', 'clima'],
    queryFn: geoApi.getClima,
    staleTime: 30 * 60 * 1000,
  });

  const trendsQ = useQuery({
    queryKey: ['intel', 'trends', uf],
    queryFn: () => intelligenceService.getTrends({ geo: `BR-${uf}`, hours: 24 }),
    enabled: Boolean(uf),
  });

  const run = useMutation({
    mutationFn: (geo: string) => intelligenceService.runQuentes(geo),
    onSettled: () => setArmedUf(null),
  });

  const resumo = cidadesQ.data?.resumo ?? {};
  const ufNomes = cidadesQ.data?.uf_nomes ?? {};
  const cfg = cfgQ.data?.estados ?? {};
  const clima = climaQ.data?.estados ?? {};
  const insights = climaQ.data?.insights ?? [];

  const maxCount = useMemo(
    () => Math.max(1, ...Object.values(resumo).map((r) => r.total)),
    [resumo],
  );

  const alertCount = useMemo(
    () => Object.values(clima).filter((c) => c.alerta?.ativo).length,
    [clima],
  );

  const cidades = uf ? cidadesQ.data?.estados[uf] ?? [] : [];


  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Mapa do Brasil</h1>
          <p className={styles.subtitle}>
            Visão geral por estado. A intensidade do bloco reflete a densidade de
            cidades-fonte; o ponto indica estados com fastnews ativa. Clica para abrir
            as cidades, ver o que está em alta ali e rodar a fastnews do estado.
          </p>
        </div>
        <span className={styles.legend}>
          {alertCount > 0 && (
            <span className={styles.legendAlert}>{alertCount} incidente(s)</span>
          )}
          <span className={styles.legendDot} /> fastnews ativa
        </span>
      </header>

      <div className={styles.topLayout}>
      <div className={styles.grid}>
        {Object.entries(TILES).map(([u, [r, c]]) => {
          const total = resumo[u]?.total ?? 0;
          const heat = 0.18 + 0.82 * (total / maxCount);
          const ativo = cfg[u]?.ativo;
          const cw = clima[u];
          const tmax = cw?.hoje?.tmax;
          const alerta = cw?.alerta?.ativo;
          const bg = alerta
            ? 'color-mix(in srgb, var(--crit) 30%, var(--bg-1))'
            : `color-mix(in srgb, var(--nexus-brand) ${Math.round(heat * 100)}%, var(--bg-1))`;
          return (
            <button
              key={u}
              type="button"
              className={`${styles.tile} ${uf === u ? styles.tileActive : ''} ${alerta ? styles.tileAlert : ''}`}
              style={{ gridRow: r, gridColumn: c, background: bg }}
              onClick={() => setUf(u)}
              title={`${ufNomes[u] ?? u}${alerta ? ` — ${cw?.alerta?.motivo}` : ''}`}
            >
              {ativo && <span className={styles.tileDot} />}
              {alerta && <span className={styles.tileAlertMark}>!</span>}
              <span className={styles.tileUf}>{u}</span>
              <span className={styles.tileTemp}>{tmax != null ? `${Math.round(tmax)}°` : '—'}</span>
            </button>
          );
        })}
      </div>

        <aside className={styles.insights}>
          <h2 className={styles.insightsTitle}>Insights de hoje</h2>
          {climaQ.isLoading && <p className={styles.muted}>A ler o tempo do país…</p>}
          {insights.map((i) => (
            <button
              key={i.chave}
              type="button"
              className={styles.insightCard}
              onClick={() => setUf(i.uf)}
            >
              <div className={styles.insightTop}>
                <span className={styles.insightTitle}>{i.titulo}</span>
                <span className={styles.insightValor}>{i.valor}</span>
              </div>
              <span className={styles.insightLocal}>{i.cidade} · {i.uf}</span>
              <span className={styles.insightSug}>{i.sugestao}</span>
            </button>
          ))}
          {!climaQ.isLoading && insights.length === 0 && (
            <p className={styles.muted}>Sem destaques no momento.</p>
          )}
        </aside>
      </div>

      {uf && (
        <div className={styles.drill}>
          <div className={styles.drillHead}>
            <h2 className={styles.drillTitle}>{ufNomes[uf] ?? uf}</h2>
            <label className={styles.ativoToggle} title="Agendamento automático por estado chega em breve — por ora use 'Rodar fastnews (lote)'">
              <input type="checkbox" checked={false} disabled />
              <span>fastnews automática (em breve)</span>
            </label>
            <div className={styles.drillSpacer} />
            <Button
              variant={armedUf === uf ? 'primary' : 'secondary'}
              onClick={() => {
                if (armedUf !== uf) {
                  setArmedUf(uf);
                  return;
                }
                run.mutate(`BR-${uf}`);
              }}
              loading={run.isPending}
              title="Roda o pipeline de Quentes do estado e gera vários rascunhos das top tendências"
            >
              {armedUf === uf ? 'Confirmar — gera vários' : 'Rodar fastnews (lote)'}
            </Button>
            <button className={styles.close} onClick={() => setUf(null)}>fechar</button>
          </div>

          {run.isError && (
            <p style={{ color: 'var(--sig-crit)', fontSize: 12, margin: '4px 0' }}>
              Falha ao rodar a fastnews: {(run.error as Error)?.message || String(run.error)}
            </p>
          )}
          {run.isSuccess && run.data && (
            <p style={{ color: 'var(--text-3)', fontSize: 12, margin: '4px 0' }}>
              {typeof run.data.ranked === 'number'
                ? `${run.data.produced?.length ?? 0} rascunho(s) gerado(s)`
                : `Ciclo pulado — ${String((run.data as { skipped?: unknown }).skipped ?? '')}`}
            </p>
          )}

          {clima[uf] && (
            <div className={styles.weather}>
              {clima[uf].alerta?.ativo && (
                <div className={styles.alertBanner}>
                  <span className={styles.alertTag}>incidente meteorológico</span>
                  <span>{clima[uf].alerta.motivo}</span>
                </div>
              )}
              {clima[uf].agora?.temp != null && (
                <div className={styles.agora}>
                  <span className={styles.agoraNow}>agora</span>
                  <span className={styles.agoraTemp}>{Math.round(clima[uf].agora.temp!)}°</span>
                  <span className={styles.agoraCond}>{clima[uf].agora.condicao}</span>
                  <span className={styles.agoraMeta}>
                    umidade {clima[uf].agora.umidade ?? '—'}% · sensação{' '}
                    {clima[uf].agora.sensacao != null ? `${Math.round(clima[uf].agora.sensacao!)}°` : '—'}
                  </span>
                </div>
              )}
              <div className={styles.weatherDays}>
                <WeatherDay label="Hoje" d={clima[uf].hoje} />
                <WeatherDay label="Amanhã" d={clima[uf].amanha} />
              </div>
              <span className={styles.weatherSrc}>
                {clima[uf].cidade} · Open-Meteo. Alerta = fora da curva histórica.
              </span>
            </div>
          )}

          <div className={styles.drillBody}>
            <div className={styles.col}>
              <span className={styles.colLabel}>Cidades-fonte ({cidades.length})</span>
              <div className={styles.chips}>
                {cidades.map((c) => (
                  <span key={c.nome} className={`${styles.chip} ${pesoClass(c.peso)}`} title={c.motivo}>
                    {c.nome}
                  </span>
                ))}
              </div>
            </div>

            <div className={styles.col}>
              <span className={styles.colLabel}>Em alta agora em {uf}</span>
              {trendsQ.isLoading && <p className={styles.muted}>a carregar…</p>}
              {trendsQ.data && (
                <ul className={styles.trends}>
                  {trendsQ.data.items.slice(0, 6).map((t) => (
                    <li key={t.query} className={styles.trendItem}>
                      <span className={styles.trendScore}>{t.score}</span>
                      <span className={styles.trendQuery}>{t.query}</span>
                      {t.flags.length > 0 && <span className={styles.trendFlag}>{t.flags[0]}</span>}
                    </li>
                  ))}
                  {trendsQ.data.items.length === 0 && (
                    <li className={styles.muted}>Sem tendências para este estado.</li>
                  )}
                </ul>
              )}
            </div>
          </div>

          {run.data && (
            <div className={styles.runResult}>
              {typeof run.data.ranked === 'number' ? (
                <p className={styles.muted}>
                  {run.data.ranked} rankeadas · {run.data.picked} escolhidas · modo {run.data.publish_status}
                </p>
              ) : (
                <p className={styles.muted}>Ciclo pulado.</p>
              )}
              {(run.data.produced ?? []).map((p) => (
                <div key={p.wp_id} className={styles.runRow}>
                  <span className={styles.runEd}>{p.editoria}</span> {p.tema}
                  <a href={p.wp_link} target="_blank" rel="noreferrer" className={styles.runLink}>
                    WP #{p.wp_id}
                  </a>
                </div>
              ))}
              {(run.data.skipped ?? []).slice(0, 4).map((s, i) => (
                <div key={i} className={styles.muted}>pulou: {s.tema} — {s.motivo}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
